import {
    Box, Chip, styled, TextField, Typography,
} from '@mui/material';
import { useState } from 'react';

export interface ChipsInputProps {
    disabled?: boolean;
    values: string[];
    onChange: (next: string[]) => void;
    label?: string;
    collapsedCount?: number;
}

function parseCommaSeparated(raw: string): string[] {
    return raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
}

const StyledChip = styled(Chip)(({ theme }) => ({
    backgroundColor: theme.palette.custom.lightBlue,
    color: theme.palette.custom.darkBlue,
    '& .MuiChip-deleteIcon': {
        color: theme.palette.custom.darkerBlue,
    },
}));

export const ChipsInput = ({
    disabled,
    values,
    onChange,
    label,
    collapsedCount = 10,
}: ChipsInputProps): JSX.Element => {
    const [input, setInput] = useState('');
    const [expanded, setExpanded] = useState(false);

    const visible = expanded ? values : values.slice(0, collapsedCount);
    const hidden = expanded ? [] : values.slice(collapsedCount);

    const addChips = (raw: string): void => {
        const tokens = parseCommaSeparated(raw);

        if (tokens.length === 0) return;

        const seen = new Set(values.map((v) => v.toLowerCase()));
        const next = [...values];

        tokens.forEach((t) => {
            const k = t.toLowerCase();

            if (!seen.has(k)) {
                next.push(t);
                seen.add(k);
            }
        });
        onChange(next);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (input.trim()) {
                addChips(input);
                setInput('');
            }
        }
    };

    const handleBlur = (): void => {
        if (input.trim()) {
            addChips(input);
            setInput('');
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
        const pasted = e.clipboardData.getData('text');

        if (pasted.includes(',')) {
            e.preventDefault();
            addChips(pasted);
        }
    };

    const removeChip = (t: string): void => {
        onChange(values.filter((v) => v !== t));
    };

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            <TextField
                fullWidth
                disabled={disabled}
                label={label}
                placeholder="Type and press Enter or paste comma-separated values"
                size="small"
                value={input}
                onBlur={handleBlur}
                onChange={(e): void => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
            />
            {values.length === 0 ? (
                <Typography color="text.secondary" variant="caption">
                    No values added yet
                </Typography>
            ) : (
                <Box alignItems="center" display="flex" flexWrap="wrap" gap={1}>
                    {visible.map((t) => (
                        <StyledChip key={t} label={t} onDelete={(): void => removeChip(t)} />
                    ))}
                    {!expanded && hidden.length > 0 && (
                    <Chip
                        label={`+${hidden.length} more (show)`}
                        variant="outlined"
                        onClick={(): void => setExpanded(true)}
                    />
                    )}
                    {expanded && values.length > collapsedCount && (
                    <Chip label="hide" variant="outlined" onClick={(): void => setExpanded(false)} />
                    )}
                </Box>
            )}
        </Box>
    );
};
